import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';

interface FinanceToolProps {
  title: string;
  description: string;
  icon: JSX.Element;
  color: string;
  comingSoon?: boolean;
  onClick?: () => void;
}

const FinanceToolCard: React.FC<FinanceToolProps> = ({ 
  title, 
  description, 
  icon, 
  color,
  comingSoon = false, 
  onClick 
}) => {
  const [isHovered, setIsHovered] = useState(false);
  
  return (
    <div 
      className={`relative transform transition-all duration-300 ${comingSoon ? 'opacity-70' : 'hover:-translate-y-2 cursor-pointer'}`}
      onClick={comingSoon ? undefined : onClick}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      {/* Card with glass morphism */}
      <div className="
        relative overflow-hidden rounded-xl bg-white/20 backdrop-blur-lg 
        border border-white/30 shadow-lg group transition-all duration-300
        hover:shadow-xl p-6 h-full
      ">
        {/* Gradient background */}
        <div className={`absolute inset-0 bg-gradient-to-br ${color} opacity-10 group-hover:opacity-20 transition-opacity`}></div>
        
        {/* Icon */}
        <div className={`
          w-12 h-12 rounded-lg mb-4 flex items-center justify-center text-white 
          bg-gradient-to-br ${color} shadow-lg group-hover:scale-110 transition-transform
        `}>
          <span className="text-xl">{icon}</span>
        </div>
        
        {/* Content */}
        <h3 className="text-xl font-bold text-gray-800 mb-2 group-hover:text-blue-600 transition-colors">
          {title}
          {comingSoon && (
            <span className="ml-2 px-2 py-1 bg-yellow-100 text-yellow-800 text-xs rounded-full">
              Coming Soon
            </span>
          )}
        </h3>
        <p className="text-gray-600 group-hover:text-gray-800 transition-colors">
          {description}
        </p>
        
        {/* Hidden action button that appears on hover */}
        {!comingSoon && (
          <div className="
            absolute bottom-4 right-4 opacity-0 group-hover:opacity-100 
            transition-opacity transform group-hover:translate-x-0 translate-x-4
          ">
            <button className="
              flex items-center justify-center w-8 h-8 rounded-full 
              bg-white/80 text-blue-600 shadow-md hover:bg-white transition-colors
            ">
              <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 5l7 7-7 7" />
              </svg>
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

const Finance: React.FC = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [hoveredTool, setHoveredTool] = useState<string | null>(null);

  // Redirect if not logged in
  React.useEffect(() => {
    if (!user) {
      navigate('/login');
    }
  }, [user, navigate]);

  if (!user) {
    return <div className="p-8 text-center">Loading...</div>;
  }

  return (
    <section className="py-24 relative overflow-hidden">
      {/* Background with gradient and mesh */}
      <div className="absolute inset-0 bg-gray-50 z-0">
        <div className="absolute inset-0 opacity-30 bg-[url('data:image/svg+xml;base64,PHN2ZyB4bWxucz0iaHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmciIHdpZHRoPSI2MCIgaGVpZ2h0PSI2MCIgdmlld0JveD0iMCAwIDYwIDYwIiBmaWxsPSJub25lIj48cGF0aCBkPSJNLjUuNWg2MHY2MEguNVYuNXptMzAgMTVhMTUgMTUgMCAwIDAtMTUgMTUgMTUgMTUgMCAwIDAgMTUgMTUgMTUgMTUgMCAwIDAgMTUtMTUgMTUgMTUgMCAwIDAtMTUtMTV6bTAgMGExNSAxNSAwIDAgMC0xNSAxNSAxNSAxNSAwIDAgMCAxNSAxNSAxNSAxNSAwIDAgMCAxNS0xNSAxNSAxNSAwIDAgMC0xNS0xNXoiIHN0cm9rZT0iIzIwMjAyMCIgc3Ryb2tlLW9wYWNpdHk9Ii4wNiIvPjwvc3ZnPg==')]"></div>
      </div>
      
      {/* Main content */}
      <div className="container relative z-10 mx-auto px-4">
        {/* Decorative elements */}
        <div className="absolute top-20 right-12 w-64 h-64 bg-green-200 rounded-full mix-blend-multiply filter blur-3xl opacity-20 animate-blob animation-delay-4000"></div>
        <div className="absolute -bottom-32 left-12 w-64 h-64 bg-teal-200 rounded-full mix-blend-multiply filter blur-3xl opacity-20 animate-blob"></div>
      
        {/* Section Title with 3D effect */}
        <div className="relative mb-16 text-center">
          <h2 className="text-4xl font-extrabold text-transparent bg-clip-text bg-gradient-to-r from-green-600 to-teal-600 inline-block mb-2">
            Financial Tools
          </h2>
          <div className="h-1 w-40 mx-auto rounded-full bg-gradient-to-r from-green-400 to-teal-500"></div>
          <p className="mt-4 text-xl text-gray-600 max-w-2xl mx-auto">Simple tools to help you make smart financial decisions for your business</p>
        </div>
      
        {/* Tools Grid with 3D Cards and Glass Morphism */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-8">
          <FinanceToolCard
            title="Price Calculator"
            description="Determine the optimal price for your products or services based on costs and market factors."
            icon={<span className="flex items-center justify-center">
              <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 7h6m0 10v-3m-3 3h.01M9 17h.01M9 14h.01M12 14h.01M15 11h.01M12 11h.01M9 11h.01M7 21h10a2 2 0 002-2V5a2 2 0 00-2-2H7a2 2 0 00-2 2v14a2 2 0 002 2z" />
              </svg>
            </span>}
            color="from-green-500 to-teal-600"
            onClick={() => navigate('/finance/price-calculator')}
          />
          
          <FinanceToolCard
            title="Profit Margin Analyzer"
            description="Calculate and visualize your profit margins to ensure your business is financially healthy."
            icon={<span className="flex items-center justify-center">
              <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 12l3-3 3 3 4-4M8 21l4-4 4 4M3 4h18M4 4h16v12a1 1 0 01-1 1H5a1 1 0 01-1-1V4z" />
              </svg>
            </span>}
            color="from-teal-400 to-cyan-600"
            onClick={() => navigate('/finance/profit-margin')}
          />
          
          <FinanceToolCard
            title="Break-Even Calculator"
            description="Find out exactly how many sales you need to cover your costs and start making profit."
            icon={<span className="flex items-center justify-center">
              <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 6l3 1m0 0l-3 9a5.002 5.002 0 006.001 0M6 7l3 9M6 7l6-2m6 2l3-1m-3 1l-3 9a5.002 5.002 0 006.001 0M18 7l3 9m-3-9l-6-2m0-2v2m0 16V5m0 16H9m3 0h3" />
              </svg>
            </span>}
            color="from-cyan-400 to-blue-500"
            onClick={() => navigate('/finance/break-even')}
          />
          
          <FinanceToolCard
            title="Cash Flow Forecaster"
            description="Project your cash flow for the next 3-6 months and identify potential cash shortfalls."
            icon={<span className="flex items-center justify-center">
              <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 9V7a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2m2 4h10a2 2 0 002-2v-6a2 2 0 00-2-2H9a2 2 0 00-2 2v6a2 2 0 002 2zm7-5a2 2 0 11-4 0 2 2 0 014 0z" />
              </svg>
            </span>}
            color="from-blue-400 to-indigo-500"
            onClick={() => navigate('/finance/cash-flow')}
          />
          
          <FinanceToolCard
            title="Pricing Strategy Advisor"
            description="Get AI-powered recommendations for pricing strategies based on your business type and goals."
            icon={<span className="flex items-center justify-center">
              <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 3.055A9.001 9.001 0 1020.945 13H11V3.055z" />
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20.488 9H15V3.512A9.025 9.025 0 0120.488 9z" />
              </svg>
            </span>}
            color="from-indigo-400 to-purple-500"
            onClick={() => navigate('/finance/pricing-strategy')}
          />
          
          <FinanceToolCard
            title="Supplier Finder"
            description="Find licensed trade suppliers for your products and services in your preferred location."
            icon={<span className="flex items-center justify-center">
              <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
              </svg>
            </span>}
            color="from-purple-400 to-pink-500"
            onClick={() => navigate('/finance/supplier-finder')}
          />

          <FinanceToolCard
            title="Risk Manager"
            description="Identify, assess, and mitigate business risks to ensure long-term success of your business."
            icon={<span className="flex items-center justify-center">
              <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
              </svg>
            </span>}
            color="from-amber-400 to-orange-500"
            onClick={() => navigate('/finance/risk-manager')}
          />
        </div>
      </div>
    </section>
  );
};

export default Finance;
